import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LocationMap = () => {
  const [activeTab, setActiveTab] = useState('map');

  const locationDetails = {
    address: "115, 116 Shree Darshan Industry Estate, Near Amrut Udhyog Nagar, Kamrej Road, Surat - 395010, Gujarat, India",
    coordinates: { lat: 21.2610001, lng: 72.9408181 },
    mapUrl: "https://www.google.com/maps/place/21%C2%B015'39.6%22N+72%C2%B056'27.0%22E/@21.2610001,72.9382432,17z/data=!3m1!4b1!4m4!3m3!8m2!3d21.2610001!4d72.9408181?entry=ttu&g_ep=EgoyMDI1MTIwOS4wIKXMDSoASAFQAw%3D%3D",
    landmarks: [
      "Shree Darshan Industry Estate, Kamrej Road",
      "Near Amrut Udhyog Nagar (Textile Hub)",
      "5 minutes from Kamrej GIDC",
      "10 minutes from Surat Railway Station"
    ],
    directions: [
      "From Surat International Airport: 20 minutes (12 km) via Dumas Road → Ring Road → Kamrej Road",
      "From Mumbai (International): 4-5 hours (280 km) via Mumbai-Ahmedabad Highway (NH48) → Take Surat exit → Follow Kamrej Road",
      "From Ahmedabad: 3-4 hours (260 km) via NH48 South → Surat exit → Kamrej Road",
      "From Surat Railway Station: 15 minutes (8 km) via Ring Road → Kamrej Road",
      "From Mumbai Airport (International Travelers): Fly to Surat Airport (1 hour) or drive via NH48",
      "Local Landmark: Located in prominent textile manufacturing zone near Kamrej GIDC"
    ]
  };

  const facilities = [
    {
      icon: 'Factory',
      title: 'Production Facility',
      description: '15,000 sq ft manufacturing space'
    },
    {
      icon: 'Car',
      title: 'Parking Available',
      description: 'Dedicated visitor parking area'
    },
    {
      icon: 'Shield',
      title: 'Security',
      description: '24/7 secured industrial complex'
    },
    {
      icon: 'Coffee',
      title: 'Meeting Room',
      description: 'Comfortable client consultation area'
    }
  ];

  const handleGetDirections = () => {
    window.open(locationDetails?.mapUrl, '_blank');
  };

  const handleDownloadLocation = () => {
    const vCardData = `BEGIN:VCARD
VERSION:3.0
FN:Kunjvihari Impex
ORG:Kunjvihari Impex
ADR:;;115, 116 Shree Darshan Industry Estate, Near Amrut Udhyog Nagar, Kamrej Road;Surat;Gujarat;395010;India
TEL:+919974056700
EMAIL:info@kunjvihariimpex.com
URL:https://kunjvihariimpex.com
END:VCARD`;

    const blob = new Blob([vCardData], { type: 'text/vcard' });
    const url = window.URL?.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'kunjvihari-impex-location.vcf';
    link?.click();
    window.URL?.revokeObjectURL(url);
  };

  return (
    <section className="py-12 lg:py-16 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 lg:mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-poppins font-bold text-text-primary mb-3 lg:mb-4">
            Visit Our Manufacturing Facility
          </h2>
          <p className="text-base lg:text-lg text-text-secondary max-w-2xl mx-auto">
            Located in the heart of Gujarat's textile hub, our state-of-the-art facility is easily accessible and ready to welcome you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Map and Navigation */}
          <div className="lg:col-span-2">
            <div className="card-elevated overflow-hidden">
              {/* Tab Navigation */}
              <div className="flex border-b border-border">
                <button
                  className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                    activeTab === 'map' ?'bg-primary text-primary-foreground' :'text-text-secondary hover:text-text-primary hover:bg-muted'
                  }`}
                  onClick={() => setActiveTab('map')}
                >
                  <Icon name="Map" size={16} className="inline mr-2" />
                  Interactive Map
                </button>
                <button
                  className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                    activeTab === 'directions' ?'bg-primary text-primary-foreground' :'text-text-secondary hover:text-text-primary hover:bg-muted'
                  }`}
                  onClick={() => setActiveTab('directions')}
                >
                  <Icon name="Navigation" size={16} className="inline mr-2" />
                  Directions
                </button>
              </div>

              {/* Map View */}
              {activeTab === 'map' && (
                <div className="relative">
                  <div className="h-96 w-full">
                    <iframe
                      width="100%"
                      height="100%"
                      loading="lazy"
                      title="Kunjvihari Impex Location"
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://www.google.com/maps?q=${locationDetails?.coordinates?.lat},${locationDetails?.coordinates?.lng}&z=14&output=embed`}
                      className="border-0"
                    />
                  </div>
                  
                  {/* Map Overlay Controls */}
                  <div className="absolute top-4 right-4 space-y-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleGetDirections}
                      iconName="Navigation"
                      iconPosition="left"
                      iconSize={16}
                    >
                      Get Directions
                    </Button>
                  </div>
                </div>
              )}

              {/* Directions View */}
              {activeTab === 'directions' && (
                <div className="p-6">
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-poppins font-semibold text-text-primary mb-4 flex items-center">
                        <Icon name="MapPin" size={20} className="text-primary mr-2" />
                        Factory Address
                      </h3>
                      <p className="text-text-secondary leading-relaxed">
                        {locationDetails?.address}
                      </p>
                    </div>

                    <div>
                      <h3 className="text-lg font-poppins font-semibold text-text-primary mb-4 flex items-center">
                        <Icon name="Landmark" size={20} className="text-secondary mr-2" />
                        Nearby Landmarks
                      </h3>
                      <ul className="space-y-2">
                        {locationDetails?.landmarks?.map((landmark, index) => (
                          <li key={index} className="flex items-center text-text-secondary">
                            <Icon name="MapPin" size={16} className="text-textile mr-2 flex-shrink-0" />
                            {landmark}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
                      <Button
                        variant="default"
                        onClick={handleGetDirections}
                        iconName="Navigation"
                        iconPosition="left"
                        className="btn-textile"
                      >
                        Open in Google Maps
                      </Button>
                      
                      <Button
                        variant="outline"
                        onClick={handleDownloadLocation}
                        iconName="Download"
                        iconPosition="left"
                      >
                        Save to Contacts
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Location Details */}
          <div className="space-y-6">
            {/* Visit Information */}
            <div className="card-elevated p-6 bg-gradient-heritage text-white">
              <h3 className="text-xl font-poppins font-semibold mb-4 flex items-center">
                <Icon name="Calendar" size={24} className="text-golden mr-2" />
                Visit Information
              </h3>
              
              <div className="space-y-3 text-sm">
                <div className="flex items-center">
                  <Icon name="Clock" size={16} className="text-golden mr-2" />
                  <span>Business Hours: Mon-Sat 9:00 AM - 7:00 PM</span>
                </div>
                <div className="flex items-center">
                  <Icon name="Clock" size={16} className="text-golden mr-2" />
                  <span>Sunday: Closed</span>
                </div>
                <div className="flex items-center">
                  <Icon name="Camera" size={16} className="text-golden mr-2" />
                  <span>Photography not allowed</span>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-white/20">
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() => document.getElementById('appointment-section')?.scrollIntoView({ behavior: 'smooth' })}
                  iconName="Calendar"
                  iconPosition="left"
                  className="bg-white text-primary hover:bg-white/90"
                >
                  Schedule Factory Visit
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LocationMap;